import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

import { FlexSpacerDirective } from '../../shared/directives/flex-spacer.directive';
import { GableDismissReason } from '../../../../services/gable/gable.service';

/**
 * Result returned by the dismiss-reason dialog: the chosen reason enum, or
 * `null` when the user backs out (Cancel / close / backdrop). A `null` result
 * ABORTS the dismissal — the proposal stays in the queue.
 */
export type DismissReasonDialogResult = GableDismissReason | null;

/** One reason choice — enum `value` paired with its human-readable `label`. */
interface DismissReasonChoice {
    value: GableDismissReason;
    label: string;
}

/**
 * V2 dialog that asks WHY a Gable suggestion is being dismissed before
 * rejecting it. Mirrors {@link ApprovalDialogComponent}'s structure (panelClass
 * `v2-dialog`, Phosphor close button, mat-dialog-* shell).
 *
 * The four reasons map to the {@link GableDismissReason} enum the reject
 * endpoint stores so the Activity feed can later show WHY each item was
 * dismissed. Picking a reason closes with that enum; Cancel closes with null.
 *
 * Icons: like the rest of Gable this dialog uses the Phosphor webfont
 * (`<i class="ph-bold ph-…">`) rather than the V2 SVG sprite — intentional,
 * matching the sibling approval dialog.
 */
@Component({
    selector: 'app-gable-dismiss-reason-dialog',
    templateUrl: './dismiss-reason-dialog.component.html',
    styleUrls: ['./dismiss-reason-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FlexSpacerDirective,
        MatDialogModule,
        MatButtonModule,
    ],
})
export class DismissReasonDialogComponent {
    /** The four dismiss reasons, in display order. Copy per contract §3. */
    readonly reasons: DismissReasonChoice[] = [
        { value: 'not_needed', label: 'Not needed right now' },
        { value: 'manual', label: 'I\'ll handle this manually' },
        { value: 'wrong', label: 'Gable got this wrong' },
        { value: 'policy', label: 'Against our policy' },
    ];

    constructor(
        private _dialogRef: MatDialogRef<DismissReasonDialogComponent, DismissReasonDialogResult>,
    ) {}

    trackByReason(_index: number, reason: DismissReasonChoice): string {
        return reason.value;
    }

    onPick(reason: GableDismissReason): void {
        this._dialogRef.close(reason);
    }

    onCancel(): void {
        this._dialogRef.close(null);
    }
}
